const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

const NUMERIC_ONLY_REGEX = /^\d*$/;

export const AUTH_CONSTANTS = {
  EMAIL_MAX_LENGTH: 254,
  PASSWORD_MIN_LENGTH: 8,
  PASSWORD_MAX_LENGTH: 256,
  CONFIRMATION_CODE_LENGTH: 6,
  NAME_MAX_LENGTH: 128,
} as const;

export function isValidEmail(email: string): boolean {
  const trimmed = email.trim();
  if (trimmed.length === 0) return false;
  if (trimmed.length > AUTH_CONSTANTS.EMAIL_MAX_LENGTH) return false;
  if (!trimmed.includes("@")) return false;
  const afterAt = trimmed.split("@")[1];
  if (!afterAt || !afterAt.includes(".")) return false;
  return EMAIL_REGEX.test(trimmed);
}

export function validateEmail(email: string): void {
  const trimmed = email.trim();
  if (trimmed.length === 0) {
    throw new Error("Please enter your email address.");
  }
  if (trimmed.length > AUTH_CONSTANTS.EMAIL_MAX_LENGTH) {
    throw new Error(
      `Email must be ${AUTH_CONSTANTS.EMAIL_MAX_LENGTH} characters or less.`
    );
  }
  if (!trimmed.includes("@")) {
    throw new Error("Please enter a valid email address (e.g., name@example.com).");
  }
  const afterAt = trimmed.split("@")[1];
  if (!afterAt || !afterAt.includes(".")) {
    throw new Error(
      "Please enter a valid email address. It should include a domain (e.g., name@example.com)."
    );
  }
  if (!EMAIL_REGEX.test(trimmed)) {
    throw new Error("Please enter a valid email address.");
  }
}

export function validateConfirmationCode(code: string): void {
  const trimmed = code.trim();
  if (trimmed.length === 0) {
    throw new Error("Please enter your verification code.");
  }
  if (!NUMERIC_ONLY_REGEX.test(trimmed)) {
    throw new Error("Verification code must contain only numbers.");
  }
  if (trimmed.length !== AUTH_CONSTANTS.CONFIRMATION_CODE_LENGTH) {
    throw new Error(
      `Verification code must be ${AUTH_CONSTANTS.CONFIRMATION_CODE_LENGTH} digits.`
    );
  }
}

export function validatePassword(password: string): void {
  if (password.length < AUTH_CONSTANTS.PASSWORD_MIN_LENGTH) {
    throw new Error(
      `Password must be at least ${AUTH_CONSTANTS.PASSWORD_MIN_LENGTH} characters.`
    );
  }
  if (password.length > AUTH_CONSTANTS.PASSWORD_MAX_LENGTH) {
    throw new Error(
      `Password must be ${AUTH_CONSTANTS.PASSWORD_MAX_LENGTH} characters or less.`
    );
  }
}

export function validateName(
  value: string,
  fieldName: string
): void {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    throw new Error(`Please enter your ${fieldName}.`);
  }
  if (trimmed.length > AUTH_CONSTANTS.NAME_MAX_LENGTH) {
    throw new Error(
      `${fieldName} must be ${AUTH_CONSTANTS.NAME_MAX_LENGTH} characters or less.`
    );
  }
}

export function trimAuthInput(value: string): string {
  return value.trim();
}

export type ValidationResult = { valid: boolean; message?: string };

export function validateEmailFormat(email: string): ValidationResult {
  try {
    validateEmail(email);
    return { valid: true };
  } catch (error) {
    return {
      valid: false,
      message: error instanceof Error ? error.message : "Invalid email address.",
    };
  }
}

export function validatePasswordLength(password: string): ValidationResult {
  try {
    validatePassword(password);
    return { valid: true };
  } catch (error) {
    return {
      valid: false,
      message: error instanceof Error ? error.message : "Invalid password.",
    };
  }
}

export function validateVerificationCode(code: string): ValidationResult {
  try {
    validateConfirmationCode(code);
    return { valid: true };
  } catch (error) {
    return {
      valid: false,
      message: error instanceof Error ? error.message : "Invalid verification code.",
    };
  }
}

export function sanitizeEmailForSubmit(email: string): string {
  return trimAuthInput(email);
}
